/**
 * Cliente de Supabase.
 *
 * Decisión de fondo: **la aplicación arranca aunque no haya backend**. Si faltan
 * las variables de entorno, `supabase` vale null y el juego funciona en modo
 * local — se juega contra la IA, sin cuenta ni colección persistente.
 *
 * Podría haber lanzado un error al arrancar, que es lo habitual. No hacerlo
 * tiene un motivo concreto: el motor y la interfaz de la Fase 1 no necesitan
 * servidor para nada, y atarlos a él significaría que nadie puede abrir el
 * proyecto, ni probar el tablero, ni ejecutar los tests sin crearse antes un
 * proyecto de Supabase. El precio es tener que preguntar `if (!supabase)` en los
 * hooks, que es barato y explícito.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database';

/**
 * Lee una variable de entorno sin dar por hecho que existe `import.meta.env`.
 *
 * Ese objeto lo inyecta Vite al empaquetar; en Node —los tests, y en la Fase 4
 * el validador de partidas del servidor, que importará módulos compartidos— no
 * existe, y `import.meta.env.LO_QUE_SEA` revienta al cargar el módulo. Un fallo
 * en tiempo de importación es de los peores: tumba el árbol entero de imports
 * antes de que se ejecute una sola línea de lógica.
 */
function readEnv(key: keyof ImportMetaEnv): string | undefined {
  return (import.meta as { env?: ImportMetaEnv }).env?.[key];
}

const url = readEnv('VITE_SUPABASE_URL');
const anonKey = readEnv('VITE_SUPABASE_ANON_KEY');

/** Hay credenciales y el backend está disponible. */
export const isSupabaseConfigured = Boolean(url && anonKey);

export type GameSupabaseClient = SupabaseClient<Database>;

export const supabase: GameSupabaseClient | null = isSupabaseConfigured
  ? createClient<Database>(url as string, anonKey as string, {
      auth: {
        // La sesión se guarda para no pedir credenciales en cada arranque. En
        // Capacitor (Fase 5) el WebView tiene localStorage propio y persistente,
        // así que esto sigue valiendo tal cual en el móvil.
        persistSession: true,
        autoRefreshToken: true,
        // Necesario para los enlaces mágicos y OAuth, que vuelven con el token
        // en el fragmento de la URL.
        detectSessionInUrl: true,
      },
      global: {
        headers: { 'x-application-name': 'triada-tcg' },
      },
    })
  : null;

/**
 * Igual que `supabase` pero lanza si no hay backend.
 *
 * Para el código que solo tiene sentido con sesión iniciada (el mercado, la
 * colección). Evita arrastrar comprobaciones de null por sitios donde llegar sin
 * backend ya sería un bug de navegación.
 */
export function requireSupabase(): GameSupabaseClient {
  if (!supabase) {
    throw new Error(
      'Supabase no está configurado. Copia .env.example a .env.local y rellena VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY.',
    );
  }
  return supabase;
}

/**
 * Traduce los errores de PostgREST a algo que se le pueda enseñar a una persona.
 *
 * Los códigos importan: `42501` es "permiso denegado", que aquí casi siempre
 * significa que una política RLS ha hecho su trabajo. Enseñar el mensaje crudo
 * de Postgres en esos casos no ayuda a nadie y encima cuenta cómo está montada
 * la base de datos.
 */
export function describeDatabaseError(error: { code?: string; message: string }): string {
  switch (error.code) {
    case '42501':
      return 'No tienes permiso para hacer eso.';
    case '23505':
      return 'Eso ya existe.';
    case '23514':
      return 'Los datos no cumplen las reglas del juego.';
    case 'PGRST116':
      return 'No se ha encontrado.';
    default:
      return error.message;
  }
}
